import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, CardHeader, Stack, Typography, Avatar, Chip,
  TextField, MenuItem, Button, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Divider, InputAdornment, Tooltip, IconButton, Checkbox, Collapse,
  Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  PersonAddAlt1 as AssignIcon, PersonSearch as SearchPersonIcon,
  CheckCircle as OkIcon, ReportProblem as WarnIcon,
  School as ClassIcon, ReceiptLong as TemplateIcon,
  AttachMoney as MoneyIcon, RestartAlt as ResetIcon,
  Wc as GenderIcon, Cake as DobIcon, Badge as IndexIcon,
  Search as SearchIcon, Refresh as RefreshIcon,
  Workspaces as ScholarshipIcon,
  RadioButtonUnchecked as PickEmptyIcon, RadioButtonChecked as PickedIcon,
  KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseRowIcon,
  DoneAll as SelectAllIcon, RemoveDone as DeselectAllIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDob = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};

const tplKey = (t) =>
  `${t.acc_yer}|${t.te || ''}|${t.item}|${t.state}`;

function SectionCard({ icon, title, subtitle, action, children, sx }) {
  return (
    <Card sx={{ mb: 2.5, ...sx }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main', width: 40, height: 40 }}>
            {icon}
          </Avatar>
        }
        title={<Typography fontWeight={800}>{title}</Typography>}
        subheader={subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        action={action}
        sx={{ pb: 1 }}
      />
      <Divider />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 10 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} noWrap>{value || '—'}</Typography>
      </Box>
    </Stack>
  );
}

export default function AssignStudentAccountPage() {
  // Reference data
  const [classes, setClasses]     = useState([]);
  const [students, setStudents]   = useState([]);
  const [studentsLoading, setSL]  = useState(false);

  // Picker / filter state
  const [search, setSearch]       = useState('');
  const [filterCla, setFilterCla] = useState('');

  // Selection
  const [picked, setPicked]       = useState(null);
  const [statusInfo, setStatus]   = useState(null);
  const [statusLoading, setStLd]  = useState(false);

  // Template config
  const [cla, setCla]             = useState('');
  const [state, setState]         = useState('Day Student');
  const [templates, setTpls]      = useState([]);
  const [tplLoading, setTplLd]    = useState(false);

  // Multi-select state
  const [selectedKeys, setSelKey] = useState([]);     // template keys checked
  const [tplLines, setTplLines]   = useState({});     // key -> { items, total }
  const [linesLoading, setLLd]    = useState({});     // key -> bool
  const [expanded, setExpanded]   = useState({});     // key -> bool

  const [submitting, setSubmit]   = useState(false);

  // Scholarship schemes — optional enrollment alongside the assignment
  const [schemes, setSchemes]     = useState([]);
  const [pickedSchemes, setPSch]  = useState([]); // array of scheme objects

  // ── Reference data ────────────────────────────────────────────
  useEffect(() => {
    API.get('/classes').then(r => setClasses(r.data || [])).catch(() => {});
    API.get('/scholarships/schemes', { params: { active: 1 } })
      .then(r => setSchemes(r.data || [])).catch(() => {});
  }, []);

  // ── Load all unassigned students ─────────────────────────────
  const loadStudents = async () => {
    setSL(true);
    try {
      const r = await API.get('/student-accounts/unassigned', {
        params: { limit: 1000, ...(filterCla ? { cla: filterCla } : {}) },
      });
      setStudents(r.data || []);
    } catch (e) { /* silent */ }
    finally { setSL(false); }
  };
  useEffect(() => { loadStudents(); /* eslint-disable-next-line */ }, [filterCla]);

  // ── Status check when a student is picked ────────────────────
  useEffect(() => {
    if (!picked) { setStatus(null); return; }
    setStLd(true);
    API.get(`/student-accounts/check/${picked.trans_code}`)
      .then(r => {
        setStatus(r.data);
        if (r.data?.student?.calss && !cla) setCla(r.data.student.calss);
      })
      .catch(() => setStatus(null))
      .finally(() => setStLd(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);

  // ── Templates list — depends on class + state ────────────────
  // After loading, every template is checked by default so the user can just
  // click Assign to apply ALL applicable templates at once. They can untick
  // any they don't want.
  useEffect(() => {
    if (!cla) {
      setTpls([]); setSelKey([]); setTplLines({}); setExpanded({});
      return;
    }
    setTplLd(true);
    API.get('/student-accounts/templates', { params: { cla, state } })
      .then((r) => {
        const list = r.data || [];
        setTpls(list);
        setSelKey(list.map(tplKey));   // default: all selected
        setTplLines({}); setExpanded({});
      })
      .catch(() => setTpls([]))
      .finally(() => setTplLd(false));
  }, [cla, state]);

  // Lazy-load line items for a single template (used on expand)
  const loadLinesFor = async (t) => {
    const key = tplKey(t);
    if (tplLines[key] || linesLoading[key]) return;
    setLLd((p) => ({ ...p, [key]: true }));
    try {
      const r = await API.get('/student-accounts/template-items', {
        params: { cla: t.cla, te: t.te || '', acc_yer: t.acc_yer, item: t.item, state: t.state },
      });
      setTplLines((p) => ({ ...p, [key]: r.data }));
    } catch { /* ignore */ }
    finally { setLLd((p) => ({ ...p, [key]: false })); }
  };

  const toggleSelect = (t) => {
    const key = tplKey(t);
    setSelKey((prev) => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const toggleExpand = (t) => {
    const key = tplKey(t);
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    loadLinesFor(t);
  };
  const selectAll   = () => setSelKey(templates.map(tplKey));
  const deselectAll = () => setSelKey([]);

  // ── Client-side search filter for the table ──────────────────
  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;
    return students.filter(s =>
      (s.firstname || '').toLowerCase().includes(term) ||
      (s.lastname  || '').toLowerCase().includes(term) ||
      (s.index_no  || '').toLowerCase().includes(term)
    );
  }, [students, search]);

  const hasAccount = !!statusInfo?.hasAccount;

  // Selected templates resolved against the templates list
  const selectedTpls = useMemo(
    () => templates.filter(t => selectedKeys.includes(tplKey(t))),
    [templates, selectedKeys]
  );
  const grandTotal = useMemo(
    () => selectedTpls.reduce((s, t) => s + Number(t.total || 0), 0),
    [selectedTpls]
  );
  const totalLineCount = useMemo(
    () => selectedTpls.reduce((s, t) => s + Number(t.line_count || 0), 0),
    [selectedTpls]
  );

  const canAssign = picked && !hasAccount && selectedTpls.length > 0 && !submitting;

  const reset = () => {
    setPicked(null); setStatus(null);
    setCla(''); setState('Day Student');
    setTpls([]); setSelKey([]); setTplLines({}); setExpanded({});
    setPSch([]);
  };

  const submit = async () => {
    if (!canAssign) return;
    const accountList = selectedTpls.map(t => `<li>${t.item} · GHS ${fmtMoney(t.total)}</li>`).join('');
    const schemeBadge = pickedSchemes.length
      ? `<div style="margin-top:8px;color:#0d9488;font-weight:700">
           + ${pickedSchemes.length} scholarship scheme(s): ${pickedSchemes.map(s => s.name).join(', ')}
         </div>`
      : '';
    const ok = await swal.confirm({
      title: 'Assign accounts?',
      tone:  'primary',
      icon:  'question',
      html:  `
        <div style="text-align:left;font-size:0.92rem">
          <div><b>${picked.firstname || ''} ${picked.lastname || ''}</b> · ${picked.index_no || ''}</div>
          <div style="margin-top:6px;color:#475569">${cla} · ${state}</div>
          <ul style="margin:10px 0 0 18px;padding:0">${accountList}</ul>
          <div style="margin-top:10px;font-weight:700">
            ${selectedTpls.length} account${selectedTpls.length === 1 ? '' : 's'} ·
            ${totalLineCount} line item${totalLineCount === 1 ? '' : 's'} ·
            GHS ${fmtMoney(grandTotal)}
          </div>
          ${schemeBadge}
        </div>`,
      confirmText: 'Yes, assign all',
    });
    if (!ok) return;

    setSubmit(true);
    try {
      const r = await API.post('/student-accounts/assign', {
        trans_code: picked.trans_code,
        templates:  selectedTpls.map(t => ({
          cla:     t.cla,
          te:      t.te || '',
          acc_yer: t.acc_yer,
          item:    t.item,
          state:   t.state,
        })),
        scheme_ids: pickedSchemes.map(s => s.id),
      });
      const schemeNote = r.data.schemes_enrolled
        ? ` + ${r.data.schemes_enrolled} scheme(s) enrolled`
        : '';
      swal.success(
        `${r.data.templates} account(s) · ${r.data.lines} line(s) · GHS ${fmtMoney(r.data.total)}${schemeNote}`,
        'Account assigned',
      );
      await loadStudents();
      reset();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not assign account'));
    } finally { setSubmit(false); }
  };

  const classOptions = useMemo(
    () => (classes || []).map(c => c.class).filter(Boolean),
    [classes]
  );

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', fontFamily: FONT_ACCT }}>
      <ModuleHeader
        icon={<AssignIcon />}
        title="Assign Student Account"
        subtitle="Pick a registered student with no account, tick every account that applies and assign in one go"
        actionLabel="Reset"
        actionIcon={<ResetIcon />}
        onAction={reset}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.35fr 1fr' }, gap: 2.5 }}>
        {/* ── LEFT — Student table ────────────────────────── */}
        <SectionCard
          icon={<SearchPersonIcon />}
          title="1 · Select Student"
          subtitle={
            studentsLoading ? 'Loading…'
            : `${filteredStudents.length} of ${students.length} student${students.length === 1 ? '' : 's'} without an account`
          }
          action={
            <Tooltip title="Refresh list">
              <span>
                <IconButton onClick={loadStudents} disabled={studentsLoading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          }
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth size="small" placeholder="Search by name or index number…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
            <TextField
              select size="small" label="Class" value={filterCla}
              onChange={(e) => setFilterCla(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value=""><em>All classes</em></MenuItem>
              {classOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, width: 36 }} />
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Index</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Class</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Gender</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentsLoading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                )}
                {!studentsLoading && filteredStudents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {students.length === 0
                        ? 'No students without an account.'
                        : 'No matches for your search.'}
                    </TableCell>
                  </TableRow>
                )}
                {!studentsLoading && filteredStudents.map((s) => {
                  const isPicked = picked?.trans_code === s.trans_code;
                  return (
                    <TableRow
                      key={s.trans_code}
                      hover
                      selected={isPicked}
                      onClick={() => setPicked(s)}
                      sx={{
                        cursor: 'pointer',
                        '&.Mui-selected': {
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
                        },
                      }}
                    >
                      <TableCell>
                        {isPicked
                          ? <PickedIcon color="primary" fontSize="small" />
                          : <PickEmptyIcon sx={{ color: 'text.disabled' }} fontSize="small" />}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light', fontSize: 12 }}>
                            {(s.firstname?.[0] || '?').toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight={isPicked ? 800 : 600}>
                            {s.firstname} {s.lastname}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={TNUM}>{s.index_no}</TableCell>
                      <TableCell>{s.calss || '—'}</TableCell>
                      <TableCell>{s.gender || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

        {/* ── RIGHT — Status / Templates / Action ─────────── */}
        <Box>
          {/* 2. Status + selected details */}
          <SectionCard
            icon={hasAccount ? <OkIcon /> : <WarnIcon />}
            title="2 · Account Status"
            subtitle="Current state of the selected student"
          >
            {!picked && (
              <Typography variant="body2" color="text.secondary">
                Click a row in the table to select a student.
              </Typography>
            )}
            {picked && statusLoading && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Checking…</Typography>
              </Stack>
            )}
            {picked && !statusLoading && statusInfo && (
              <>
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, fontWeight: 700 }}>
                      {(picked.firstname?.[0] || '?').toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={800} noWrap>
                        {picked.firstname} {picked.lastname}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {picked.index_no}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    gap: 1.2,
                  }}>
                    <DetailRow icon={<IndexIcon  fontSize="small" />} label="Index"  value={picked.index_no} />
                    <DetailRow icon={<GenderIcon fontSize="small" />} label="Gender" value={picked.gender} />
                    <DetailRow icon={<DobIcon    fontSize="small" />} label="DOB"    value={fmtDob(picked.dob)} />
                    <DetailRow icon={<ClassIcon  fontSize="small" />} label="Class"  value={picked.calss} />
                  </Box>
                </Box>

                {hasAccount ? (
                  <Alert severity="success" icon={<OkIcon />}>
                    <Typography fontWeight={700}>Account Already Exists</Typography>
                    <Typography variant="body2">
                      {statusInfo.feeCount} fee line(s) on record · Total
                      <Box component="span" sx={{ ml: 0.5, fontWeight: 800, ...TNUM }}>
                        GHS {fmtMoney(statusInfo.feeTotal)}
                      </Box>. The form below is disabled.
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="warning" icon={<WarnIcon />}>
                    <Typography fontWeight={700}>No Account Assigned</Typography>
                    <Typography variant="body2">
                      Tick every account that applies below — they will all be assigned in one shot.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </SectionCard>

          {/* 3. Templates — multi-select */}
          <SectionCard
            icon={<TemplateIcon />}
            title="3 · Admission Templates"
            subtitle={
              templates.length > 0
                ? `${selectedTpls.length} of ${templates.length} selected`
                : 'Pick a class and student type to load templates'
            }
            action={
              templates.length > 0 && (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Select all">
                    <span>
                      <IconButton size="small" onClick={selectAll}
                        disabled={!picked || hasAccount || selectedKeys.length === templates.length}>
                        <SelectAllIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Deselect all">
                    <span>
                      <IconButton size="small" onClick={deselectAll}
                        disabled={!picked || hasAccount || selectedKeys.length === 0}>
                        <DeselectAllIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              )
            }
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
              <TextField
                select label="Class" value={cla} onChange={(e) => setCla(e.target.value)}
                disabled={!picked || hasAccount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><ClassIcon fontSize="small" /></InputAdornment>
                  ),
                }}
              >
                <MenuItem value=""><em>— Select class —</em></MenuItem>
                {classOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>

              <TextField
                select label="Student Type" value={state} onChange={(e) => setState(e.target.value)}
                disabled={!picked || hasAccount}
              >
                <MenuItem value="Day Student">Day Student</MenuItem>
                <MenuItem value="Boarder">Boarder</MenuItem>
              </TextField>
            </Box>

            {/* Templates list */}
            {!cla ? (
              <Typography variant="body2" color="text.secondary">
                Pick a class to see available account templates.
              </Typography>
            ) : tplLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading templates…</Typography>
              </Stack>
            ) : templates.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No templates set up for <b>{cla}</b> · {state}. Set them up in <b>Admission Details</b> first.
              </Alert>
            ) : (
              <Box>
                {templates.map((t) => {
                  const key = tplKey(t);
                  const isSel = selectedKeys.includes(key);
                  const isExp = !!expanded[key];
                  const lines = tplLines[key];
                  const lLoad = !!linesLoading[key];
                  return (
                    <Paper key={key} variant="outlined" sx={{
                      mb: 1, overflow: 'hidden',
                      borderColor: isSel ? 'primary.main' : 'divider',
                      bgcolor: isSel ? (t2) => alpha(t2.palette.primary.main, 0.04) : 'background.paper',
                      transition: 'all 0.15s',
                    }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.1, pr: 1 }}>
                        <Checkbox size="small" checked={isSel}
                          disabled={!picked || hasAccount}
                          onChange={() => toggleSelect(t)} />
                        <Avatar sx={{
                          width: 30, height: 30,
                          bgcolor: (t2) => alpha(t2.palette.primary.main, 0.12),
                          color: 'primary.main',
                        }}>
                          <TemplateIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {t.item}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {t.acc_yer} · {t.state} · {t.line_count} item{t.line_count === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                        <Chip size="small"
                          label={`GHS ${fmtMoney(t.total)}`}
                          color={isSel ? 'primary' : 'default'}
                          variant={isSel ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 800, ...TNUM }} />
                        <Tooltip title={isExp ? 'Hide items' : 'Show items'}>
                          <IconButton size="small" onClick={() => toggleExpand(t)}>
                            {isExp ? <CollapseRowIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Collapse in={isExp} unmountOnExit>
                        <Divider />
                        <Box sx={{ p: 1.2, pl: 6, bgcolor: (t2) => alpha(t2.palette.primary.main, 0.02) }}>
                          {lLoad ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                              <CircularProgress size={14} />
                              <Typography variant="caption" color="text.secondary">Loading…</Typography>
                            </Stack>
                          ) : !lines || (lines.items?.length || 0) === 0 ? (
                            <Typography variant="caption" color="text.disabled">
                              No line items.
                            </Typography>
                          ) : (
                            <Table size="small" sx={{ '& td': { py: 0.4, border: 0 } }}>
                              <TableBody>
                                {lines.items.map((it, i) => (
                                  <TableRow key={it.trans_code}>
                                    <TableCell sx={{ width: 24, color: 'text.disabled', ...TNUM }}>{i + 1}</TableCell>
                                    <TableCell>{it.narr}</TableCell>
                                    <TableCell align="right" sx={{ ...TNUM, fontWeight: 600 }}>
                                      {fmtMoney(it.amount)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}

                {/* Grand total */}
                <Paper variant="outlined" sx={{
                  mt: 1.5, px: 2, py: 1.2,
                  bgcolor: (t2) => alpha(t2.palette.success.main, 0.06),
                  borderColor: 'success.light',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 10 }}>
                      Grand Total
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {selectedTpls.length} account(s) · {totalLineCount} line(s)
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={900} color="success.dark" sx={TNUM}>
                    GHS {fmtMoney(grandTotal)}
                  </Typography>
                </Paper>
              </Box>
            )}
          </SectionCard>

          {/* 4. Scholarship schemes (optional) */}
          <SectionCard
            icon={<ScholarshipIcon />}
            title="4 · Scholarship Scheme (optional)"
            subtitle={
              pickedSchemes.length
                ? `${pickedSchemes.length} scheme(s) will be enrolled with this assignment`
                : 'Auto-applies the scheme percentage to every account it covers'
            }
          >
            {schemes.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No active schemes. Define one in <b>Scholarship / Adjustment</b> first if you want to enroll this student now.
              </Alert>
            ) : (
              <>
                <Autocomplete
                  multiple size="small" disableCloseOnSelect
                  options={schemes}
                  value={pickedSchemes}
                  onChange={(_, v) => setPSch(v)}
                  getOptionLabel={(o) => o.name || ''}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={!picked || hasAccount}
                  renderOption={(props, o, { selected }) => (
                    <li {...props} key={o.id}>
                      <Checkbox size="small" checked={selected} sx={{ mr: 0.5 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontSize={13} fontWeight={700}>{o.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.line_count} account line(s){o.description ? ` - ${o.description}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((o, i) => (
                      <Chip key={o.id} size="small" label={o.name}
                        color="primary" variant="outlined"
                        {...getTagProps({ index: i })} />
                    ))
                  }
                  renderInput={(p) => (
                    <TextField {...p}
                      label="Pick scheme(s) to enroll on"
                      placeholder={pickedSchemes.length ? '' : 'e.g. Staff Children Bursary, Third Child...'}
                      InputProps={{ ...p.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><ScholarshipIcon fontSize="small" /></InputAdornment>
                            {p.InputProps.startAdornment}
                          </>
                        ) }} />
                  )}
                />
                {pickedSchemes.length > 0 && (
                  <Alert severity="success" variant="outlined" sx={{ mt: 1.5, py: 0.4 }}>
                    On assignment, the scheme percentage stamps onto every covered account immediately, and stays on for future bills until you remove the enrollment.
                  </Alert>
                )}
              </>
            )}
          </SectionCard>

          {/* 5. Action */}
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between">
                <Box>
                  <Typography fontWeight={800}>Ready to assign?</Typography>
                  <Typography variant="caption" color="text.secondary">
                    All selected accounts are applied in one transaction. After this, the student leaves the unassigned list.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Clear form">
                    <span>
                      <IconButton onClick={reset} disabled={submitting}><ResetIcon /></IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AssignIcon />}
                    onClick={submit}
                    disabled={!canAssign}
                    sx={{ fontWeight: 800, px: 3 }}
                  >
                    {submitting ? 'Assigning…'
                      : selectedTpls.length > 0
                        ? `Assign ${selectedTpls.length} Account${selectedTpls.length === 1 ? '' : 's'}`
                        : 'Assign Account'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
